/*
  GEMDRAW — Membership passes

  Makes each card respond to light. The material behind the crest slides one
  way, the crest slides the other, a pool in the tier's own metal follows the
  cursor, and one metallic sweep crosses the emblem as the pointer arrives.

  IT IS A PARALLAX, NOT A TILT. The card used to rotate a couple of degrees
  toward the pointer. Two planes disagreeing slightly about where they are
  gives the same sense of depth with none of the cost: no 3D context, no
  perspective feedback, no shearing at the edges of a wide card, and nothing
  that reads as a gimmick when three of them do it at once.

  It also owns the two things the benefit drawings cannot do for themselves:
  stopping while the section is off screen, and answering immediately when one
  of them is reached for. Everything else about those drawings — what moves,
  how far and for how long — is CSS, and this file cannot change any of it.

  THE ENTRANCE IS NOT HERE. The row is staggered by the theme's own reveal
  engine, one IntersectionObserver for the whole page marking each element once.
  Building a second observer for that would be another thing to keep in step
  for no gain.

  EVERYTHING IT DOES IS A CUSTOM PROPERTY. The script's entire output is four
  values and two attributes per card. All the drawing lives in
  gemdraw-membership.css, so the look can be retuned without opening this file
  and this file cannot leak layout.

  PERFORMANCE. Pointer events fire faster than the screen refreshes, so moves
  are coalesced into one requestAnimationFrame write per frame and the handler
  only records coordinates. Each card's box is measured on enter and on resize,
  never inside a move — getBoundingClientRect in a pointermove is the classic
  way to make a smooth effect stutter.

  NO LIBRARIES. Pointer events, one IntersectionObserver, one rAF and CSS
  custom properties.
*/

(function () {
  'use strict';

  const SELECTOR = '[data-gd-mship-card]';
  const BODY = '.gd-mship__body';

  /*
    The section itself, and the benefit lines inside it. Both are here for the
    benefit drawings: the section is watched so their cycles can be stopped
    while nobody is looking at them, and a line is watched so its own drawing
    can answer when it is reached for.
  */
  const SECTION = '[data-gd-mship-section]';
  const BENEFIT = '.gd-mship__benefit';
  const ICON = '.gd-mship__benefit-icon';
  const IDLE = 'data-gd-mship-idle';

  /*
    The sweep is on the emblem, not on the card. A band crossing the whole card
    washes the type as it passes and reads as a screen effect; masked to the
    emblem's own artwork it reads as light moving across metal, which is the
    only thing on the card actually made of metal.
  */
  const EMBLEM = '[data-gd-mship-emblem]';

  const LIT = 'data-gd-mship-lit';
  const PASS = 'data-gd-mship-pass';

  /*
    Every response here is a hover response, and only a fine pointer can hover.
    A touch screen gets the reveal, the stack and the static materials, all of
    which are in the stylesheet — there is deliberately no touch gesture,
    because a card that swallowed drags in the middle of a long page would be a
    trap and there is nothing behind it worth trapping for.
  */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function Card(el) {
    this.el = el;
    this.body = el.querySelector(BODY);
    this.emblem = el.querySelector(EMBLEM);

    this.box = null;
    this.x = 0;
    this.y = 0;
    this.frame = 0;

    /* One sweep per visit. Re-armed on leave rather than cleared on enter, so a
       cursor crossing twice gets two sweeps and a cursor resting gets one. */
    this.armed = true;
  }

  /* Measured on enter and on resize, never during a move. */
  Card.prototype.measure = function () {
    this.box = this.el.getBoundingClientRect();
  };

  Card.prototype.write = function () {
    this.frame = 0;
    if (!this.box || !this.box.width || !this.box.height) return;

    const px = (this.x - this.box.left) / this.box.width;
    const py = (this.y - this.box.top) / this.box.height;

    /*
      Published as -1 to 1 from the centre rather than as pixels or degrees, so
      the stylesheet decides how far each plane travels and in which direction.
      The crest multiplies it by three and the material by minus three; that
      opposition is the entire effect and it is one line of CSS each.
    */
    const style = this.el.style;
    style.setProperty('--gd-membership-dx', ((px - 0.5) * 2).toFixed(3));
    style.setProperty('--gd-membership-dy', ((py - 0.5) * 2).toFixed(3));

    /* The pool sits under the cursor. Clamped, because a pointer can be over
       the card's margin and the light should stay on the material. */
    style.setProperty('--gd-membership-x', (Math.min(1, Math.max(0, px)) * 100).toFixed(1) + '%');
    style.setProperty('--gd-membership-y', (Math.min(1, Math.max(0, py)) * 100).toFixed(1) + '%');
  };

  Card.prototype.schedule = function () {
    if (this.frame) return;
    const card = this;
    this.frame = window.requestAnimationFrame(function () {
      card.write();
    });
  };

  Card.prototype.rest = function () {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    /* Cleared rather than zeroed, so the card falls back to the stylesheet's
       own resting values — the CSS stays the single source of truth for what
       "at rest" means. */
    const style = this.el.style;
    style.removeProperty('--gd-membership-dx');
    style.removeProperty('--gd-membership-dy');
    style.removeProperty('--gd-membership-x');
    style.removeProperty('--gd-membership-y');
  };

  /*
    ONE METALLIC SWEEP.

    The attribute comes off on animationend rather than being tracked in a flag,
    so the animation is genuinely one-shot: it cannot be re-armed while running
    and it leaves nothing behind when it finishes.
  */
  Card.prototype.pass = function () {
    if (!this.emblem || !this.armed || reduceMotion.matches) return;
    this.armed = false;

    const emblem = this.emblem;
    emblem.removeAttribute(PASS);

    /* Reading offsetWidth forces the removal to apply before the attribute goes
       back on. Without it the browser coalesces both changes and the animation
       never restarts. */
    void emblem.offsetWidth;
    emblem.setAttribute(PASS, '');
  };

  function bind(el) {
    if (el.dataset.gdMshipBound === 'true') return;

    const body = el.querySelector(BODY);
    if (!body) return;

    el.dataset.gdMshipBound = 'true';

    const card = new Card(el);

    if (card.emblem) {
      card.emblem.addEventListener('animationend', function () {
        card.emblem.removeAttribute(PASS);
      });
    }

    el.addEventListener('pointerenter', function (event) {
      if (event.pointerType !== 'mouse' || !finePointer.matches) return;

      card.measure();
      el.setAttribute(LIT, '');
      card.pass();
    });

    el.addEventListener(
      'pointermove',
      function (event) {
        if (event.pointerType !== 'mouse' || !finePointer.matches) return;
        if (reduceMotion.matches) return;

        card.x = event.clientX;
        card.y = event.clientY;
        card.schedule();
      },
      { passive: true }
    );

    el.addEventListener('pointerleave', function () {
      el.removeAttribute(LIT);
      card.rest();

      /* Re-armed here, which is what makes the sweep once per visit rather than
         once per page. */
      card.armed = true;
    });

    return card;
  }

  /*
    THE DRAWINGS STOP WHEN THE SECTION IS NOT ON SCREEN.

    Fifteen benefit marks, three emblems, three glints, a marquee and two
    passing highlights is a lot of small compositor work to leave running on a
    page someone has scrolled past. One observer for every membership section
    on the document writes a single attribute, and the stylesheet pauses every
    cycle under it — see the visibility block in gemdraw-membership.css.

    THE DEFAULT IS RUNNING, NOT PAUSED. The attribute is only ever written to
    stop things. If this file never loads, or the observer never fires, the
    section animates exactly as authored rather than sitting frozen — a
    performance optimisation must not be able to break the thing it optimises.

    The margin is generous on purpose: the cards resume a screen-height before
    they are reached, so nothing is ever caught starting up mid-view.
  */
  let idleObserver = null;

  function getIdleObserver() {
    if (idleObserver || !('IntersectionObserver' in window)) return idleObserver;

    idleObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.removeAttribute(IDLE);
          } else {
            entry.target.setAttribute(IDLE, 'rest');
          }
        });
      },
      { rootMargin: '100% 0px' }
    );

    return idleObserver;
  }

  const sections = [];

  function watchSection(el) {
    if (el.dataset.gdMshipWatched === 'true') return;

    const io = getIdleObserver();
    if (!io) return;

    el.dataset.gdMshipWatched = 'true';
    sections.push(el);
    io.observe(el);
  }

  /*
    THE GESTURE ANSWERS WHEN THE BENEFIT IS REACHED FOR.

    Each drawing idles on a long cycle with a per-card, per-row delay, which is
    what keeps fifteen marks from ever moving together. The cost of that is
    that pointing at one and waiting is unsatisfying: its next gesture could be
    seven seconds away.

    So a pointer over a line, or focus landing inside one, sets that line's
    animations back to their own start and they play immediately. The clock is
    rewound on the animations already running rather than a second set of
    keyframes being layered on top — same gesture, same timing, no duplication
    and nothing left behind when the pointer leaves.

    currentTime is set to the effect's delay rather than to zero, because a CSS
    animation's timeline includes its delay: zero would put every drawing back
    into its own stagger and produce no visible response at all. The delays
    here are negative, so this lands a little way into the gesture — which is
    what an immediate answer should look like.
  */
  let lastPoked = null;

  function poke(item) {
    if (!item || reduceMotion.matches) return;

    const svg = item.querySelector(ICON);
    if (!svg || typeof svg.getAnimations !== 'function') return;

    svg.getAnimations({ subtree: true }).forEach(function (animation) {
      if (!animation.effect || typeof animation.effect.getComputedTiming !== 'function') return;
      const timing = animation.effect.getComputedTiming();
      animation.currentTime = timing.delay || 0;
    });
  }

  /*
    Delegated, so a section with fifteen lines costs two listeners rather than
    thirty — and so lines rendered later by the theme editor are covered
    without rebinding anything.
  */
  function bindSection(el) {
    if (el.dataset.gdMshipSectionBound === 'true') return;
    el.dataset.gdMshipSectionBound = 'true';

    el.addEventListener('pointerover', function (event) {
      if (event.pointerType !== 'mouse') return;

      const item = event.target.closest(BENEFIT);
      /* Moving within one line fires repeatedly; only crossing into a
         different one is a new arrival. */
      if (!item || item === lastPoked) return;

      lastPoked = item;
      poke(item);
    });

    el.addEventListener('pointerleave', function () {
      lastPoked = null;
    });

    /* Keyboard parity. Nothing inside a line is focusable today, so this is
       inert — and it is what makes the response correct the moment a benefit
       ever carries a link, rather than something to remember to add then. */
    el.addEventListener('focusin', function (event) {
      const item = event.target.closest(BENEFIT);
      if (!item) return;

      lastPoked = item;
      poke(item);
    });
  }

  const cards = [];

  function init(root) {
    const scope = root || document;

    scope.querySelectorAll(SELECTOR).forEach(function (el) {
      const card = bind(el);
      if (card) cards.push(card);
    });

    scope.querySelectorAll(SECTION).forEach(function (el) {
      watchSection(el);
      bindSection(el);
    });

    /* A section re-rendered by the editor is its own root, so it is not found
       by the query above. */
    if (scope !== document && scope.matches && scope.matches(SECTION)) {
      watchSection(scope);
      bindSection(scope);
    }
  }

  /*
    A measured box goes stale when the page reflows or scrolls. Re-measured on
    the next frame rather than on every event, and only for the card a pointer
    is actually on; every other card measures again on its own next enter.
  */
  let resizeFrame = 0;
  function remeasure() {
    if (resizeFrame) return;
    resizeFrame = window.requestAnimationFrame(function () {
      resizeFrame = 0;
      cards.forEach(function (card) {
        if (card.el.hasAttribute(LIT)) card.measure();
      });
    });
  }

  window.addEventListener('resize', remeasure, { passive: true });
  window.addEventListener('scroll', remeasure, { passive: true });

  /* The preference can change while the page is open. Every card resolves to
     its still state immediately. */
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () {
      if (!reduceMotion.matches) return;
      cards.forEach(function (card) {
        card.el.removeAttribute(LIT);
        card.rest();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The theme editor replaces a section's markup wholesale, so anything bound
     to the old nodes is gone with them. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /*
    EVERYTHING HELD ON THE OLD NODES IS RELEASED.

    Per-card listeners die with the elements they were attached to, but the two
    module-level registries do not: without this, every edit of this section in
    the theme editor would leave another set of detached cards in the resize
    loop and another detached section under the observer, and the editor is
    exactly where a section gets re-rendered dozens of times in a session.
  */
  document.addEventListener('shopify:section:unload', function () {
    for (let i = cards.length - 1; i >= 0; i -= 1) {
      if (!document.contains(cards[i].el)) cards.splice(i, 1);
    }

    for (let i = sections.length - 1; i >= 0; i -= 1) {
      if (document.contains(sections[i])) continue;
      if (idleObserver) idleObserver.unobserve(sections[i]);
      sections.splice(i, 1);
    }

    if (lastPoked && !document.contains(lastPoked)) lastPoked = null;
  });
})();
